// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CitedClaimsPanel.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
import { ArrowSquareOut } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Preview } from "../../../components/Preview";
import { PdfCitationPreview } from "../../../routes/settings/routes/knowledge-database/components/PdfCitationPreview";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  knowledgeDatabaseRoute,
} from "../../../routes/settings/routes/knowledge-database/route";
import { useSourceFileQuery } from "../../../utils/queries/knowledge";
import {
  type TicketMessageCitation,
  isKnowledgeRuleCitation,
} from "../utils/commentSourceUtils";

/**
 * Pins the cited line inside the source PDF, reusing the KDB admin viewer's
 * bbox/pin renderer. Rendered only when the citation carries a `pageNumber`
 * location and its source is a PDF; anything else (spreadsheet, unanchored,
 * download error) falls back silently to the quote-only view above it. Its own
 * component so `useSourceFileQuery` is an unconditional hook.
 */
function KnowledgeRuleCitationPin({
  sourceId,
  pageNumber,
  bbox,
  relX,
  relY,
}: {
  sourceId: string;
  pageNumber: number;
  bbox?: { x: number; y: number; w: number; h: number };
  relX?: number;
  relY?: number;
}) {
  const { data, isLoading } = useSourceFileQuery(sourceId);
  const { t } = useTranslation("ui");
  if (isLoading) {
    return (
      <Text
        size="1"
        className="text-puntt-neutral-gray-11"
        data-testid="comment-knowledge-rule-citation-pin-loading"
      >
        {t("commentCitationLinks.loadingSource")}
      </Text>
    );
  }
  // Non-PDF sources (spreadsheets) or a failed download: keep the quote-only
  // citation rather than showing a broken viewer.
  if (!data || data.mimeType !== "application/pdf") return null;
  return (
    <PdfCitationPreview
      data={data.bytes}
      pageNumber={pageNumber}
      bbox={bbox ?? null}
      relX={relX}
      relY={relY}
      testIdPrefix="comment-knowledge-rule-citation"
    />
  );
}

/**
 * Shared Knowledge Database rule citation dialog — statement headline, quote
 * excerpt, and deep link into the Rules tab. Used by comment chips and the
 * Cited Claims rail so both surfaces stay in sync.
 */
export function KnowledgeRuleCitationDialog({
  citation,
  open,
  onOpenChange,
  children,
}: {
  citation: TicketMessageCitation;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional Dialog.Trigger (or other) children. Omit for controlled-only open. */
  children?: ReactNode;
}) {
  const { t } = useTranslation("ui");
  if (!isKnowledgeRuleCitation(citation)) return null;

  const statement = citation.statement?.trim();
  const quote = citation.quote?.trim();
  const sourceName = citation.sourceName?.trim();
  // A page-anchored citation renders the source PDF below the quote. In the
  // default 600px / 420px-tall dialog the page is squeezed into a slit and the
  // cited line lands ~10px tall, which reads as "blurry, can't tell what it
  // points at". Give the document view room; keep the compact box for
  // quote-only citations so an empty dialog is not oversized.
  const hasPagePin = Boolean(
    citation.sourceId && citation.location?.pageNumber,
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
      <Dialog.Content
        data-testid="comment-knowledge-rule-citation-dialog"
        maxWidth={hasPagePin ? "min(920px, 92vw)" : undefined}
        className={
          hasPagePin
            ? "flex max-h-[85vh] flex-col gap-4 overflow-y-auto"
            : "flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto"
        }
      >
        <Dialog.Title>
          {t("commentCitationLinks.knowledgeRuleSource")}
        </Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.knowledgeRuleExcerpt")}
        </Dialog.Description>
        {statement ? (
          <Text size="2" weight="medium" data-testid="knowledge-rule-statement">
            {statement}
          </Text>
        ) : null}
        {sourceName ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid="knowledge-rule-citation-source-doc"
          >
            {sourceName}
          </Text>
        ) : null}
        {quote ? (
          <>
            <Text size="2" className="text-puntt-neutral-gray-11">
              <em>{t("commentCitationLinks.fromKnowledgeDatabase")}</em>
            </Text>
            <Preview text={quote} />
          </>
        ) : null}
        {citation.sourceId && citation.location?.pageNumber ? (
          <KnowledgeRuleCitationPin
            sourceId={citation.sourceId}
            pageNumber={citation.location.pageNumber}
            bbox={citation.location.bbox}
            relX={citation.location.rel_x}
            relY={citation.location.rel_y}
          />
        ) : null}
        <Flex justify="end">
          <Button variant="outline" size="2" asChild>
            <Link
              data-testid="comment-knowledge-rule-citation-view-link"
              to={knowledgeDatabaseRoute.to}
              search={{
                ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                tab: "rules",
                ruleId: citation.ruleId,
                ...(citation.sourceId ? { sourceId: citation.sourceId } : {}),
              }}
              target="_blank"
              rel="noreferrer"
              className="puntt-link flex gap-1"
            >
              <Text>
                {t("commentCitationLinks.viewRuleInKnowledgeDatabase")}
              </Text>
              <ArrowSquareOut width={16} height={16} />
            </Link>
          </Button>
        </Flex>
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function knowledgeRuleCitationLabel(
  citation: TicketMessageCitation,
  fallback: string,
): string {
  if (!isKnowledgeRuleCitation(citation)) return fallback;
  return citation.statement?.trim() || citation.quote?.trim() || fallback;
}
